import React, { useState, Children } from 'react';
import { useFormContext } from 'react-hook-form';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, EditIcon, AlertCircleIcon } from 'lucide-react';
interface FormSummaryProps {
  onEditStep: (stepIndex: number) => void;
}
const FormSummary: React.FC<FormSummaryProps> = ({
  onEditStep
}) => {
  const {
    watch
  } = useFormContext();
  const [expandedSections, setExpandedSections] = useState<string[]>(['clientInfo', 'spouseInfo', 'existingDocuments', 'beneficiaries', 'specialNeeds', 'assets', 'financialPOA', 'healthcarePOA']);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const formData = watch();
  const toggleSection = (section: string) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };
  const isSectionExpanded = (section: string) => expandedSections.includes(section);
  const SummarySection = ({
    title,
    section,
    stepIndex,
    children
  }: {
    title: string;
    section: string;
    stepIndex: number;
    children: React.ReactNode;
  }) => <div className="border border-slate-200 rounded-md mb-4 overflow-hidden" data-id="element-52">
      <div className="flex justify-between items-center w-full px-4 py-3 bg-slate-50" data-id="element-53">
        <button type="button" className="flex items-center flex-1 text-left" onClick={() => toggleSection(section)} data-id="element-54">
          <h3 className="font-medium text-navy-800 mr-2" data-id="element-55">{title}</h3>
          {isSectionExpanded(section) ? <ChevronUpIcon size={20} className="text-navy-600" data-id="element-56" /> : <ChevronDownIcon size={20} className="text-navy-600" data-id="element-57" />}
        </button>
        <button type="button" onClick={() => onEditStep(stepIndex)} className="flex items-center text-sm text-navy-600 hover:text-navy-800 ml-4" data-id="element-58">
          <EditIcon size={16} className="mr-1" data-id="element-59" />
          Edit
        </button>
      </div>
      {isSectionExpanded(section) && <div className="p-4 bg-white" data-id="element-60">{children}</div>}
    </div>;
  const SummaryItem = ({
    label,
    value
  }: {
    label: string;
    value: any;
  }) => {
    if (!value || value === '') return null;
    return <div className="py-2 border-b border-slate-100 last:border-0" data-id="element-61">
        <div className="text-sm text-slate-500" data-id="element-62">{label}</div>
        <div className="font-medium mt-1" data-id="element-63">{value}</div>
      </div>;
  };
  const EmptyState = () => <div className="text-center py-4 text-slate-500 italic" data-id="element-64">
      No information provided
    </div>;
  return <div data-id="element-65">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6 flex items-start" data-id="element-66">
        <AlertCircleIcon className="text-blue-500 mr-3 mt-0.5 flex-shrink-0" size={20} data-id="element-67" />
        <div data-id="element-68">
          <h3 className="font-medium text-blue-800" data-id="element-69">Review Your Information</h3>
          <p className="text-blue-700 mt-1" data-id="element-70">
            Please carefully review all the information you have provided below.
            You can edit any section by clicking the "Edit" button next to each
            section heading. Once you have confirmed everything is correct,
            check the confirmation box at the bottom and submit your form.
          </p>
        </div>
      </div>
      {/* Client Information */}
      <SummarySection title="1. Client Information" section="clientInfo" stepIndex={0} data-id="element-71">
        {formData.clientInfo?.fullName ? <div className="space-y-1" data-id="element-72">
            <SummaryItem label="Existing Client" value={formData.clientInfo?.existingClient === 'true' ? 'Yes' : 'No'} data-id="element-73" />
            <SummaryItem label="How did you hear about us?" value={formData.clientInfo?.referralSource} data-id="element-74" />
            <SummaryItem label="Full Name" value={formData.clientInfo?.fullName} data-id="element-75" />
            <SummaryItem label="Age" value={formData.clientInfo?.age} data-id="element-76" />
            <SummaryItem label="Marital Status" value={formData.clientInfo?.maritalStatus} data-id="element-77" />
            <SummaryItem label="Address" value={formData.clientInfo?.address} data-id="element-78" />
            <SummaryItem label="Phone Number" value={formData.clientInfo?.phone} data-id="element-79" />
            <SummaryItem label="Email" value={formData.clientInfo?.email} data-id="element-80" />
            <SummaryItem label="Occupation" value={formData.clientInfo?.occupation} data-id="element-81" />
            <SummaryItem label="Employer Name" value={formData.clientInfo?.employerName} data-id="element-82" />
            <SummaryItem label="Business Address" value={formData.clientInfo?.businessAddress} data-id="element-83" />
            <SummaryItem label="Business Phone" value={formData.clientInfo?.businessPhone} data-id="element-84" />
            <SummaryItem label="Citizenship" value={formData.clientInfo?.citizenship} data-id="element-85" />
          </div> : <EmptyState data-id="element-86" />}
      </SummarySection>
      {/* Spouse & Family */}
      <SummarySection title="2. Spouse & Family" section="spouseInfo" stepIndex={1} data-id="element-87">
        {formData.spouseInfo?.name || formData.previousMarriages || formData.children || formData.grandchildren ? <div className="space-y-4" data-id="element-88">
            {formData.spouseInfo?.name && <div data-id="element-89">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-90">
                  Spouse Information
                </h4>
                <div className="space-y-1" data-id="element-91">
                  <SummaryItem label="Spouse's Name" value={formData.spouseInfo?.name} data-id="element-92" />
                  <SummaryItem label="Spouse's Age" value={formData.spouseInfo?.age} data-id="element-93" />
                </div>
              </div>}
            {formData.previousMarriages && <div data-id="element-94">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-95">
                  Previous Marriages
                </h4>
                <div className="space-y-1" data-id="element-96">
                  <SummaryItem label="Previously Married" value={formData.previousMarriages?.exists === 'true' ? 'Yes' : 'No'} data-id="element-97" />
                  {formData.previousMarriages?.exists === 'true' && <SummaryItem label="Ex-Spouse Name" value={formData.previousMarriages?.exSpouseName} data-id="element-98" />}
                </div>
              </div>}
            {formData.domesticContracts && <div data-id="element-99">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-100">
                  Domestic Contracts
                </h4>
                <div className="space-y-1" data-id="element-101">
                  <SummaryItem label="Has Domestic Contracts" value={formData.domesticContracts?.exists === 'true' ? 'Yes' : 'No'} data-id="element-102" />
                </div>
              </div>}
            {formData.supportObligations && <div data-id="element-103">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-104">
                  Support Obligations
                </h4>
                <div className="space-y-1" data-id="element-105">
                  <SummaryItem label="Has Support Obligations" value={formData.supportObligations?.exists === 'true' ? 'Yes' : 'No'} data-id="element-106" />
                  {formData.supportObligations?.exists === 'true' && <SummaryItem label="Details" value={formData.supportObligations?.details} data-id="element-107" />}
                </div>
              </div>}
            {formData.children && formData.children.length > 0 && <div data-id="element-108">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-109">Children</h4>
                {formData.children.map((child: any, index: number) => <div key={index} className="mb-2 pl-4 border-l-2 border-slate-200" data-id="element-110">
                    <SummaryItem label="Name" value={child.name} data-id="element-111" />
                    <SummaryItem label="Age" value={child.age} data-id="element-112" />
                  </div>)}
              </div>}
            {formData.grandchildren && formData.grandchildren.length > 0 && <div data-id="element-113">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-114">
                  Grandchildren
                </h4>
                {formData.grandchildren.map((grandchild: any, index: number) => <div key={index} className="mb-2 pl-4 border-l-2 border-slate-200" data-id="element-115">
                      <SummaryItem label="Name" value={grandchild.name} data-id="element-116" />
                      <SummaryItem label="Age" value={grandchild.age} data-id="element-117" />
                      <SummaryItem label="Parent" value={grandchild.parent} data-id="element-118" />
                    </div>)}
              </div>}
            {formData.deceasedChildren && <div data-id="element-119">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-120">
                  Deceased Children
                </h4>
                <div className="space-y-1" data-id="element-121">
                  <SummaryItem label="Has Deceased Children" value={formData.deceasedChildren?.exists === 'true' ? 'Yes' : 'No'} data-id="element-122" />
                  {formData.deceasedChildren?.exists === 'true' && <SummaryItem label="Names" value={formData.deceasedChildren?.names} data-id="element-123" />}
                </div>
              </div>}
          </div> : <EmptyState data-id="element-124" />}
      </SummarySection>
      {/* Existing Estate Documents */}
      <SummarySection title="3. Existing Estate Documents" section="existingDocuments" stepIndex={2} data-id="element-125">
        {formData.existingDocuments || formData.estateTrustee || formData.alternateTrustee ? <div className="space-y-4" data-id="element-126">
            {formData.existingDocuments && <div data-id="element-127">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-128">
                  Existing Will
                </h4>
                <div className="space-y-1" data-id="element-129">
                  <SummaryItem label="Has Existing Will" value={formData.existingDocuments?.hasWill === 'true' ? 'Yes' : 'No'} data-id="element-130" />
                </div>
              </div>}
            {formData.estateTrustee && <div data-id="element-131">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-132">
                  Estate Trustee
                </h4>
                <div className="space-y-1" data-id="element-133">
                  <SummaryItem label="Name" value={formData.estateTrustee?.name} data-id="element-134" />
                  <SummaryItem label="Relationship" value={formData.estateTrustee?.relationship} data-id="element-135" />
                  <SummaryItem label="City" value={formData.estateTrustee?.city} data-id="element-136" />
                </div>
              </div>}
            {formData.alternateTrustee && <div data-id="element-137">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-138">
                  Alternate Trustee
                </h4>
                <div className="space-y-1" data-id="element-139">
                  <SummaryItem label="Name" value={formData.alternateTrustee?.name} data-id="element-140" />
                  <SummaryItem label="Relationship" value={formData.alternateTrustee?.relationship} data-id="element-141" />
                  <SummaryItem label="City" value={formData.alternateTrustee?.city} data-id="element-142" />
                </div>
              </div>}
          </div> : <EmptyState data-id="element-143" />}
      </SummarySection>
      {/* Beneficiaries */}
      <SummarySection title="4. Beneficiaries" section="beneficiaries" stepIndex={3} data-id="element-144">
        {formData.beneficiaries || formData.specificGifts || formData.guardians ? <div className="space-y-4" data-id="element-145">
            {formData.specificGifts && formData.specificGifts.length > 0 && <div data-id="element-146">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-147">
                  Specific Gifts or Bequests
                </h4>
                {formData.specificGifts.map((gift: any, index: number) => <div key={index} className="mb-2 pl-4 border-l-2 border-slate-200" data-id="element-148">
                    <SummaryItem label="Item/Amount" value={gift.item} data-id="element-149" />
                    <SummaryItem label="Recipient" value={gift.recipient} data-id="element-150" />
                  </div>)}
              </div>}
            {formData.beneficiaries && <div data-id="element-151">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-152">
                  Estate Distribution
                </h4>
                <div className="space-y-1" data-id="element-153">
                  <SummaryItem label="Distribution Type" value={formData.beneficiaries?.distribution} data-id="element-154" />
                  {formData.beneficiaries?.distribution === 'custom' && <SummaryItem label="Custom Plan" value={formData.beneficiaries?.customPlan} data-id="element-155" />}
                  <SummaryItem label="Alternate Beneficiaries" value={formData.beneficiaries?.alternates} data-id="element-156" />
                  <SummaryItem label="Age of Inheritance" value={formData.beneficiaries?.ageOfInheritance} data-id="element-157" />
                  {formData.beneficiaries?.ageOfInheritance === 'custom' && <SummaryItem label="Custom Age" value={formData.beneficiaries?.customAge} data-id="element-158" />}
                </div>
              </div>}
            {formData.guardians && <div data-id="element-159">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-160">
                  Guardians for Minor Children
                </h4>
                <div className="space-y-1" data-id="element-161">
                  <SummaryItem label="Guardian" value={formData.guardians?.name} data-id="element-162" />
                  <SummaryItem label="Alternate Guardian" value={formData.guardians?.alternate} data-id="element-163" />
                  <SummaryItem label="Monthly Allowance" value={formData.guardians?.monthlyAllowance ? `$${formData.guardians.monthlyAllowance}` : undefined} data-id="element-164" />
                </div>
              </div>}
          </div> : <EmptyState data-id="element-165" />}
      </SummarySection>
      {/* Special Needs */}
      <SummarySection title="5. Corporate, Dependents & Special Needs" section="specialNeeds" stepIndex={4} data-id="element-166">
        <EmptyState data-id="element-167" />
      </SummarySection>
      {/* Assets & Insurance */}
      <SummarySection title="6. Assets & Insurance" section="assets" stepIndex={5} data-id="element-168">
        <EmptyState data-id="element-169" />
      </SummarySection>
      {/* Financial POA */}
      <SummarySection title="7. Power of Attorney: Financial" section="financialPOA" stepIndex={6} data-id="element-170">
        <EmptyState data-id="element-171" />
      </SummarySection>
      {/* Healthcare POA */}
      <SummarySection title="8. Power of Attorney: Healthcare" section="healthcarePOA" stepIndex={7} data-id="element-172">
        <EmptyState data-id="element-173" />
      </SummarySection>
      {/* Confirmation Section */}
      <div className="mt-8 bg-navy-50 p-6 rounded-lg border border-navy-200" data-id="element-174">
        <h3 className="text-lg font-medium text-navy-900 mb-4" data-id="element-175">
          Ready to Submit?
        </h3>
        <p className="text-navy-700 mb-6" data-id="element-176">
          By submitting this form, you confirm that all information provided is
          accurate to the best of your knowledge. Your lawyer will review this
          information and may contact you for clarification if needed.
        </p>
        <div className="flex items-start" data-id="element-177">
          <input type="checkbox" id="terms-confirm" checked={isConfirmed} onChange={e => setIsConfirmed(e.target.checked)} className="h-5 w-5 text-navy-600 rounded focus:ring-navy-500 mt-0.5" data-id="element-178" />
          <label htmlFor="terms-confirm" className="ml-3 text-navy-700" data-id="element-179">
            <span className="font-medium" data-id="element-180">
              I confirm that I have reviewed all the information above and it is
              accurate and complete.
            </span>
            <span className="block text-sm text-navy-600 mt-1" data-id="element-181">
              You must check this box before you can submit the form.
            </span>
          </label>
        </div>
        {!isConfirmed && <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3 flex items-start" data-id="element-182">
            <AlertCircleIcon size={16} className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-183" />
            <p className="text-sm text-yellow-800" data-id="element-184">
              Please review all sections and check the confirmation box above to
              enable the submit button.
            </p>
          </div>}
      </div>
      {/* Hidden input to control form submission */}
      <input type="hidden" value={isConfirmed ? 'true' : 'false'} data-id="element-185" />
    </div>;
};
export default FormSummary;